import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { BookableAppointmentTypeDeletionValidation } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/bookable_appointment_type.pb';
import { SortOrder } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/sort.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { useModalControl, useDebouncedValue, useAlert } from '@frontend/design-system';
import { AppointmentTypeBulkColorAssignmentBanner } from '../../components/banners/appointment-type-bulk-color-assignment-banner';
import { AddEditServiceTypeTray } from '../../components/service-type-modal';
import { AppointmentTypeGroupsTable } from '../../components/ServiceTypesV3/AppointmentTypeGroupsTable';
import { DeleteServiceTypeModal } from '../../components/ServiceTypesV3/DeleteServiceTypeModal';
import { useAppointmentTypesCustomizationAndFeatureFlagsHelper } from '../../hooks/use-appointment-types-customization-and-feature-flags-helper';
import { useCheckPaymentsEnabled } from '../../hooks/use-check-payments-enabled';
import { useGetSourceDropdownFilterDetails } from '../../hooks/use-get-source-dropdown-filter-details';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { useServiceTypesHelperShallowStore } from '../../stores/use-service-types-helper-store';
import { ServiceTypesSettingsTrackingIds } from '../../trackingIds/service-types-settings-tracking';

type ServiceTypeV3PageProps = {
  locationId?: string;
  isIntegratedOffice?: boolean;
};

export const ServiceTypeV3Page = ({ locationId, isIntegratedOffice = false }: ServiceTypeV3PageProps) => {
  const { t } = useTranslation('schedule');
  const alerts = useAlert();
  const { selectedLocationIds, isMultiLocation } = useSchedulingLocationInfo();
  const selectedLocationId = locationId || selectedLocationIds[0];

  const { selectedSourceIds, setSelectedSourceIds, sourceOptions, hasMultipleSources } =
    useGetSourceDropdownFilterDetails();

  const { setPaymentsEnabled, setIsOnlineSchedulingEnabled } = useServiceTypesHelperShallowStore(
    'setPaymentsEnabled',
    'setIsOnlineSchedulingEnabled'
  );

  const [selectedServiceType, setSelectedServiceType] = useState<SchedulerV3.Types.SelectedServiceType | null>(null);
  const [deleteValidation, setDeleteValidation] = useState<BookableAppointmentTypeDeletionValidation>();

  const { modalProps: editModalProps, triggerProps: editModalTriggerProps } = useModalControl();
  const { modalProps: deleteModalProps, triggerProps: deleteModalTriggerProps } = useModalControl();

  const [pageConfig, setPageConfig] = useState<SchedulerV3.Helpers.ServiceTypePageConfig>({
    page: 1,
    limit: 50,
    searchValue: '',
    sortBy: 'display_name',
    sortDir: SortOrder.ASC,
  });

  const debouncedSearchValue = useDebouncedValue(pageConfig.searchValue, 400);

  const { data: sourceTenantsData } = SchedulerV3.Queries.useListSourceTenantsQuery({
    request: { locationId: selectedLocationId },
  });

  // NOTE: This is needed as API requires sourceTenantIds to filter service types. This can be changed in future when API is updated to accept sourceIds for filtering.
  const selectedSourceTenantIds = useMemo(
    () =>
      (sourceTenantsData?.sources ?? [])
        .filter((source) => source.id && selectedSourceIds.includes(source.id))
        .flatMap((source) => {
          const siteTenantIds =
            source.sites?.flatMap((site) => (site.sourceTenantId ? [site.sourceTenantId] : [])) ?? [];
          return source.sourceTenantId ? [...siteTenantIds, source.sourceTenantId] : siteTenantIds;
        }),
    [selectedSourceIds, sourceTenantsData]
  );

  const { data: serviceTypesData, isLoading: isLoadingServiceTypesData } = SchedulerV3.Helpers.useGetServiceTypesV3Data(
    {
      debouncedSearchValue,
      selectedLocationId,
      pageConfig,
      isIntegratedOffice,
      sourceTenantIds: selectedSourceTenantIds,
    }
  );

  const { isLoading: isLoadingCFInfo, isOnlineSchedulingEnabled } =
    useAppointmentTypesCustomizationAndFeatureFlagsHelper(selectedLocationId);

  const { isPaymentsEnabled } = useCheckPaymentsEnabled(selectedLocationId);
  const { mutateAsync: validateDeleteServiceType } =
    SchedulerV3.Mutations.useValidateBookableAppointmentTypesDeletionMutation();

  useEffect(() => {
    setIsOnlineSchedulingEnabled(isOnlineSchedulingEnabled);
    setPaymentsEnabled(isPaymentsEnabled);
  }, [isPaymentsEnabled, isOnlineSchedulingEnabled, setIsOnlineSchedulingEnabled, setPaymentsEnabled]);

  const handleOnCreate = (selectedRow: SchedulerV3.Types.SelectedServiceType) => {
    if (!selectedRow.appointmentTypeGroup) return;

    SchedulerV3.trackPendoEvent(ServiceTypesSettingsTrackingIds.events.addServiceToAppointmentTypeClicked, {
      locationId: selectedLocationId,
    });

    setSelectedServiceType(selectedRow);
    editModalTriggerProps.onClick();
  };

  const handleOnEdit = (selectedRow: SchedulerV3.Types.SelectedServiceType) => {
    if (!selectedRow.appointmentTypeGroup) return;

    SchedulerV3.trackPendoEvent(ServiceTypesSettingsTrackingIds.events.serviceEditClicked, {
      isIntegrated: isIntegratedOffice,
      locationId: selectedLocationId,
    });

    setSelectedServiceType(selectedRow);
    editModalTriggerProps.onClick();
  };

  const handleDeleteClick = async (selectedRow: SchedulerV3.Types.SelectedServiceType) => {
    if (!selectedRow.appointmentTypeGroup || !selectedRow.bookableAppointmentType) return;
    setSelectedServiceType(selectedRow);
    const validateResponse = await validateDeleteServiceType({
      bookableAppointmentTypeIds: [selectedRow.bookableAppointmentType.id],
      locationId: selectedRow.bookableAppointmentType.locationId || '',
    });
    const firstValidation =
      validateResponse.validations && validateResponse.validations.length > 0
        ? validateResponse.validations[0]
        : undefined;
    if (!firstValidation) {
      alerts.error(t('Error in deleting the service type. Please try again.'));
      return;
    }
    setDeleteValidation(firstValidation);
    deleteModalTriggerProps.onClick();
  };

  const deleteModalOnClose = () => {
    setSelectedServiceType(null);
    deleteModalProps.onClose();
  };

  const editModalOnClose = () => {
    setSelectedServiceType(null);
    editModalProps.onClose();
  };

  const isLoading = isLoadingServiceTypesData || isLoadingCFInfo;

  return (
    <section css={containerStyle}>
      <AppointmentTypeBulkColorAssignmentBanner locationIds={[selectedLocationId]} />
      <div css={{ margin: theme.spacing(3) }}>
        <AppointmentTypeGroupsTable
          isIntegratedOffice={isIntegratedOffice}
          locationId={selectedLocationId}
          serviceTypesData={serviceTypesData?.appointmentTypeGroups || []}
          totalCount={serviceTypesData?.totalAppointmentCount ?? 0}
          isLoading={isLoading}
          pageConfig={pageConfig}
          setPageConfig={setPageConfig}
          onCreate={handleOnCreate}
          onEdit={handleOnEdit}
          onDelete={handleDeleteClick}
          hasMultipleSources={hasMultipleSources}
          sourceOptions={sourceOptions}
          setSelectedSourceIds={setSelectedSourceIds}
          isPaymentsEnabled={isPaymentsEnabled}
          debouncedSearchValue={debouncedSearchValue}
          isMultiLocation={isMultiLocation}
          editModalTriggerProps={editModalTriggerProps}
        />
        {editModalProps.show && (
          <AddEditServiceTypeTray
            variant={isIntegratedOffice ? 'integrated' : 'core'}
            locationId={selectedLocationId}
            selectedServiceDetails={selectedServiceType}
            modalProps={editModalProps}
            onClose={editModalOnClose}
          />
        )}

        <DeleteServiceTypeModal
          modalProps={deleteModalProps}
          serviceType={selectedServiceType}
          onCancel={deleteModalOnClose}
          isIntegratedOffice={isIntegratedOffice}
          isMultiLocation={isMultiLocation}
          deleteValidation={deleteValidation}
        />
      </div>
    </section>
  );
};

const containerStyle = css({
  position: 'relative',
  background: theme.colors.white,
  borderRadius: theme.borderRadius.medium,
  boxShadow: theme.shadows.light,
  overflow: 'auto',
});
